import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import axios from 'axios';

const SystemStatusContext = createContext();

export const useSystemStatus = () => {
  const context = useContext(SystemStatusContext);
  if (!context) {
    throw new Error('useSystemStatus must be used within a SystemStatusProvider');
  }
  return context;
};

export const SystemStatusProvider = ({ children }) => {
  const [systemStatus, setSystemStatus] = useState({
    isHealthy: true,
    isLoading: true,
    lastCheck: null,
    error: null,
    services: {}
  });

  const checkSystemHealth = useCallback(async () => {
    try {
      const baseUrl = process.env.REACT_APP_API_BASE_URL || '';
      const response = await axios.get(`${baseUrl}/health`, {
        timeout: 5000
      });

      const data = response.data;
      
      const isHealthy = data.status === 'healthy' || data.status === 'degraded';
      const isCriticalUnhealthy = data.status === 'unhealthy';
      
      setSystemStatus({
        isHealthy: isHealthy && !isCriticalUnhealthy,
        isLoading: false,
        lastCheck: new Date(),
        error: isCriticalUnhealthy ? '系统关键服务不可用，部分功能可能无法使用' : null,
        services: data.services || {},
        status: data.status
      });
    } catch (error) {
      setSystemStatus({
        isHealthy: false,
        isLoading: true,
        lastCheck: new Date(),
        error: '无法连接到服务器，请检查网络连接或稍后重试',
        services: {},
        status: 'unhealthy'
      });
    }
  }, []);

  // 初始检查
  useEffect(() => {
    checkSystemHealth();
  }, [checkSystemHealth]);

  // 定期检查（每30秒检查一次）
  useEffect(() => {
    const interval = setInterval(() => {
      checkSystemHealth();
    }, 30000); // 30秒

    return () => clearInterval(interval);
  }, [checkSystemHealth]);

  // 手动刷新
  const refreshStatus = useCallback(() => {
    setSystemStatus(prev => ({ ...prev, isLoading: true }));
    checkSystemHealth();
  }, [checkSystemHealth]);

  const value = {
    ...systemStatus,
    refreshStatus,
    checkSystemHealth
  };

  return (
    <SystemStatusContext.Provider value={value}>
      {children}
    </SystemStatusContext.Provider>
  );
};

